import { Stack } from '@mui/material';
import { differenceInSeconds } from 'date-fns';
import { maxBy } from 'lodash';
import { useMemo } from 'react';

import { Detection, useSoundPressureReadings } from '@/api/historicalData';
import { sensorKey } from '@/api/historicalData/helpers';
import { Timeline } from '@/charts/timelineChart';
import { SpotIcon } from '@/components/data/ColorIcon';
import { Datum } from '@/components/data/Datum';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { DataDetailDatum } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import { DataDetailTimestampRange } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailSectionTimestamp';
import { MissingDataInfo } from '@/components/spotter/historicalDataTab/datumDetail/MissingDataInfo';
import { SelectableTimestampBox } from '@/components/spotter/historicalDataTab/datumDetail/SelectableTimestampBox';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { isFiniteNumber } from '@/utils';

const DetectionDensityDatum = ({ value }: { value: number }) => (
    <Datum value={value} type="ratio" sourceUnit="%" targetUnit="%" maxDecimalPlaces={0} />
);

const DetectionDensityIcon = ({ color, value }: { color: string; value: number }) => (
    <SpotIcon color={color} sx={{ opacity: value / 100 }} />
);

export const DetectionDatumDetail = ({
    data,
    line,
}: {
    data: Detection | null;
    dataset: Detection[];
    line: Timeline<Detection>;
}) => {
    const dateRange = useMemo(
        () => (data ? { startDate: new Date(data.start_time), endDate: new Date(data.end_time) } : null),
        [data]
    );

    const { spotterId } = useSpotterContext();
    const { data: allSensorData, isPending: splPending } = useSoundPressureReadings({
        spotterId,
        dateRange,
        /*
         * Only `broadbandSpl` is read below, but the bands keep this window on the same cache entry as the
         * spectrogram covering the same tab: the exclusion reaches the query key, so asking for less here
         * would mean a second copy of the same hour of readings.
         */
        includeBands: true,
    });

    // probably would be better to look up by BM node id
    const sensorPosition = data?.sensor_position;

    const sensorData = useMemo(() => {
        if (!allSensorData || !sensorPosition) {
            return null;
        }
        if (Object.keys(allSensorData).length === 1) {
            // if there's only one hydrophone, just use it
            return Object.values(allSensorData)[0];
        }
        const key = sensorKey(sensorPosition);
        if (key in allSensorData) {
            return allSensorData[key];
        }

        console.warn(`No SPL data found at sensor position ${sensorPosition}`);
        return null;
    }, [allSensorData, sensorPosition]);

    const maxSpl = useMemo(() => maxBy(sensorData, 'broadbandSpl')?.broadbandSpl, [sensorData]);

    if (!data || !dateRange) return false;

    const value = line.valueKey && (data[line.valueKey] as number);

    return (
        <>
            <DataDetailTimestampRange dateRange={dateRange} unit="datetime" />
            <Stack sx={{ m: 1 }}>
                {isFiniteNumber(value) && (
                    <DataDetailDatum
                        label={line.valueLabel}
                        icon={<DetectionDensityIcon color={line.color} value={value} />}
                    >
                        <DetectionDensityDatum value={value} />
                    </DataDetailDatum>
                )}
                <DataDetailDatum label="Max sound pressure level">
                    <LoadingIndicatorIcon isLoading={splPending}>
                        {maxSpl ? (
                            <Datum value={maxSpl} type="soundPressureLevel" sourceUnit="dB" />
                        ) : (
                            <MissingDataInfo>High-resolution SPL data not available</MissingDataInfo>
                        )}
                    </LoadingIndicatorIcon>
                </DataDetailDatum>
            </Stack>
        </>
    );
};

export const DetectionDataSummary = ({ dataset, line }: { dataset: Detection[]; line: Timeline<Detection> }) => {
    const { dateRange } = useDateRangeContext();

    const { totalDurationSec, maxDensity, maxDensityTimestamp, avgDensity } = useMemo(() => {
        let totalDurationSec = 0;
        let maxDensity = 0;
        let maxDensityTimestamp: string | null = null;
        let densitySum = 0;
        let densityCount = 0;
        dataset.forEach((datum) => {
            if (!line.valueKey || (line.dataFilter && !line.dataFilter(datum))) {
                return;
            }

            const density = datum[line.valueKey] as number;
            totalDurationSec += differenceInSeconds(datum.end_time, datum.start_time) * (density / 100);
            if (density > maxDensity) {
                maxDensity = density;
                maxDensityTimestamp = datum.start_time;
            }
            densitySum += density;
            densityCount++;
        });
        if (totalDurationSec > 3600) {
            // if it's more than an hour, round to the nearest minute
            totalDurationSec = 60 * Math.round(totalDurationSec / 60);
        }
        return { totalDurationSec, maxDensity, maxDensityTimestamp, avgDensity: densitySum / densityCount };
    }, [dataset, line]);

    if (!dataset.length) {
        return false;
    }

    const { color } = line;
    const hasDetections = totalDurationSec >= 1;

    return (
        <>
            {dateRange && <DataDetailTimestampRange dateRange={dateRange} />}
            <Stack sx={{ m: 1 }}>
                <DataDetailDatum
                    label={
                        hasDetections
                            ? 'Total duration of detections'
                            : 'No detections were received within this time range.'
                    }
                >
                    {hasDetections && (
                        <Datum value={totalDurationSec} type="time" sourceUnit="s" sx={{ whiteSpace: 'wrap' }} />
                    )}
                </DataDetailDatum>
                {maxDensityTimestamp && (
                    <SelectableTimestampBox timestamp={maxDensityTimestamp}>
                        <DataDetailDatum
                            label={`Max ${line.valueLabel.toLowerCase()}`}
                            icon={<DetectionDensityIcon color={color} value={maxDensity} />}
                        >
                            <DetectionDensityDatum value={maxDensity} />
                        </DataDetailDatum>
                    </SelectableTimestampBox>
                )}
                {isFinite(avgDensity) && (
                    <DataDetailDatum
                        label={`Averaged ${line.valueLabel.replace(/detection /i, '').toLowerCase()}`}
                        icon={<DetectionDensityIcon color={color} value={avgDensity} />}
                    >
                        <DetectionDensityDatum value={avgDensity} />
                    </DataDetailDatum>
                )}
            </Stack>
        </>
    );
};
